import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { compose } from "recompose";
import EnhanceHeader from "./EnhanceHeader";
import HeaderQuery from "./HeaderQuery.gql";
import HeaderUserQuery from "./HeaderUserQuery.gql";
import { withShop } from "web/core/shop/ShopContext";
import Logo from "theme/components/atoms/Logo";
import Navigation from "./Navigation/Navigation";
import HeaderLinksMobile from "./Navigation/HeaderLinksMobile";
import MenuMainCategories from "./Navigation/Menu/MenuMainCategories";
import useSubNavigation from "theme/layouts/Header/useSubNavigation";
import { withErrorBoundary } from "theme/components/helpers/ErrorBoundary/ErrorBoundary";
import SimpleHeader from "theme/layouts/Header/SimpleHeader";
import { injectIntl } from "react-intl";
import { useShop } from "web/core/shop/ShopContext";
import { IconButton } from "theme/components/atoms/Button";
import SearchNavigation from "theme/layouts/Header/Navigation/SearchNavigation";
import { useIntl, defineMessages } from "react-intl";
import AdvertisingBanner from "theme/components/organisms/AdvertisingBanner/AdvertisingBanner";
import SecondBanner from "theme/components/organisms/SecondBanner/SecondBanner";
import breakpoints from "theme/components/atoms/Breakpoints";

const messages = defineMessages({
  search: {
    id: "modules.Header.search",
    defaultMessage: "Search",
  },
  logoAlt: {
    id: "layout.Header.logoAlt",
    defaultMessage: "Robertet",
  },
});

const refreshHeaderDisplay = () => {
  const header = document.querySelector(".header");
  const headerSkeleton = document.querySelector(".header__skeleton");
  const menuMainCategories = document.querySelector(".header__central");

  if (header instanceof HTMLElement && headerSkeleton instanceof HTMLElement) {
    requestAnimationFrame(() => {
      setTimeout(() => {
        // Vérifier si le menu principal est chargé
        if (!menuMainCategories || menuMainCategories.children.length === 0) {
          // Si le menu n'est pas encore chargé, réessayer plus tard
          setTimeout(refreshHeaderDisplay, 100);
          return;
        }

        const height = header.offsetHeight;
        headerSkeleton.style.height = `${height}px`;
        document.documentElement.style.setProperty(
          "--header-height",
          `${height}px`
        );

        if (height > 0) {
          requestAnimationFrame(() => {
            header.classList.add("loaded");
          });
        }
      }, 50);
    });
  }
};

const Header = (props) => {
  const intl = useIntl();
  const shop = useShop();

  const [subNavRef, selectMenu, renderSubNav] = useSubNavigation();
  let baseLink = "/";
  if (shop.locale === "en-GB" || shop.locale === "en-US") {
    baseLink = "/en/";
  }

  if (shop.id === "us_en") {
    baseLink = "/us/";
  }

  const [isDesktop, setIsDesktop] = React.useState(false);

  useEffect(() => {
    const checkIfDesktop = () => {
      setIsDesktop(window.innerWidth >= breakpoints.desktop);
    };

    checkIfDesktop();
    window.addEventListener("resize", checkIfDesktop);

    return () => {
      window.removeEventListener("resize", checkIfDesktop);
    };
  }, []);

  useEffect(() => {
    // Premier calcul rapide
    refreshHeaderDisplay();

    // Recalculer avec des délais plus longs pour s'assurer que le menu est chargé
    const timers = [200, 500, 1000].map((delay) =>
      setTimeout(refreshHeaderDisplay, delay)
    );

    // Observer les changements de taille du header
    const resizeObserver = new ResizeObserver(refreshHeaderDisplay);

    const header = document.querySelector(".header");
    if (header) {
      resizeObserver.observe(header);
    }

    window.addEventListener("scroll", refreshHeaderDisplay);

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      if (header) {
        resizeObserver.unobserve(header);
      }
      resizeObserver.disconnect();
      window.removeEventListener("scroll", refreshHeaderDisplay);
    };
  }, []);

  return (
    <>
      <header className="header">
        <AdvertisingBanner data={props.data} />
        <SecondBanner
          isActive={props.data.second_banner_is_active}
          text={props.data.second_banner_text}
          backgroundColor={props.data.second_banner_background_color}
          textColor={props.data.second_banner_text_color}
        />
        <div className="container header__container">
          <div className="header__links mobile-only">
            <HeaderLinksMobile
              data={props.data}
              userData={props.userData}
              selectMenu={selectMenu}
              renderSubNav={renderSubNav}
              enterpriseMenuLink={props.data.enterprise_menu_link}
              storeView={props.user?.storeView}
            />
          </div>
          <div className="header__logo">
            <a href={baseLink} onClick={() => selectMenu(null)}>
              <Logo alt={intl.formatMessage(messages.logoAlt)} />
            </a>
          </div>

          <div className="header__search">
            <IconButton
              icon="magnifier"
              title={intl.formatMessage(messages.search)}
              size="none"
              onClick={() => {
                if (isDesktop) {
                  const searchInput = document.querySelector("#search");
                  if (searchInput instanceof HTMLElement) {
                    searchInput.focus();
                  }
                }
              }}
            />
            {isDesktop && <SearchNavigation />}
          </div>

          <div className="header__links">
            <Navigation
              data={props.data}
              userData={props.userData}
              selectMenu={selectMenu}
              renderSubNav={renderSubNav}
              storeView={props.user?.storeView}
              canDisplayLinkOnListing={props.data.canDisplayLinkOnListing}
            />
          </div>
        </div>
        <div className="header__central desktop-only">
          <MenuMainCategories
            data={props.data}
            selectMenu={selectMenu}
            renderSubNav={renderSubNav}
            enterpriseMenuLink={props.data.enterprise_menu_link}
            applicationVersion={props.data.application_version}
          />
        </div>
      </header>
      <div className="header__skeleton"></div>
      <div className="header__sub" ref={subNavRef}></div>
    </>
  );
};

const SmartHeader = compose(
  withErrorBoundary(SimpleHeader),
  withShop(),
  EnhanceHeader({
    HeaderQuery,
    HeaderUserQuery,
  }),
  injectIntl
)(Header);

SmartHeader.propTypes = {
  categories: PropTypes.array,
  user: PropTypes.object,
};

export default SmartHeader;
